/**
 * Mọi chữ tiếng Việt + màu của trang Bảng tin (docs/bang-tin/03-admin.md §14).
 *
 * Cách gọi người dùng theo chủ dự án (28/09/2026): "Creator" (không dùng "Idol"),
 * "Creator NPC" chỉ cho Creator làm nội dung NPC, nhân sự nội bộ = "Nhân viên",
 * fan/khách = "User". Lớp STAFF của backend (người Admin đã xác nhận trong
 * feed_members) hiện là "Nhân viên" — tài liệu thiết kế gọi là "NPC".
 */
import { colors } from 'theme/tokens';
import { FeedClass, FeedTier, RestrictionScope, WordSeverity } from './types';

export interface PillStyle {
  label: string;
  color: string;
  bg: string;
}

const tone = (label: string, color: string, bg: string): PillStyle => ({ label, color, bg });

// ------------------------------------------------------------------ trạng thái

export const POST_STATUS_STYLE: Record<string, PillStyle> = {
  PUBLISHED: tone('Đang hiện', colors.successInk, colors.successSoft),
  PENDING_REVIEW: tone('Đang giữ chờ duyệt', colors.warningInk, colors.warningSoft),
  HIDDEN: tone('Đã ẩn', colors.dangerInk, colors.dangerSoft),
  REJECTED: tone('Bị từ chối', colors.textSecondary, colors.surfaceMuted),
  FAILED: tone('Video lỗi', colors.dangerInk, colors.dangerSoft),
  DELETED: tone('Đã xoá', colors.textMuted, colors.surfaceMuted),
  ACTIVE: tone('Đang hoạt động', colors.successInk, colors.successSoft),
};

export const NEEDS_REVIEW_STYLE = tone('Cần xem', colors.warningInk, colors.warningSoft);
export const UNPUBLIC_STYLE = tone('Đã gỡ khỏi công khai', colors.textSecondary, colors.surfaceMuted);

/** Trạng thái của nội dung bị báo cáo (D14 `targetStatus`), nhìn từ phía hàng chờ. */
export const REPORT_TARGET_STATUS_LABEL: Record<string, string> = {
  PUBLISHED: 'Đang hiện',
  PENDING_REVIEW: 'Đang giữ chờ duyệt',
  HIDDEN: 'Đã ẩn',
  REJECTED: 'Bị từ chối',
  FAILED: 'Video lỗi',
  DELETED: 'Đã xoá',
  ACTIVE: 'Tài khoản đang hoạt động',
};

export const HIDDEN_REASON_LABEL: Record<string, string> = {
  REPORTS: 'Nhiều người báo cáo',
  AI_BLOCK: 'Máy chặn (AI)',
  MODERATOR: 'Quản lý ẩn',
};

export const MEDIA_STATUS_STYLE: Record<string, PillStyle> = {
  QUEUED: tone('Chờ xử lý', colors.infoInk, colors.infoSoft),
  PROCESSING: tone('Đang xử lý', colors.infoInk, colors.infoSoft),
  READY: tone('Sẵn sàng', colors.successInk, colors.successSoft),
  FAILED: tone('Lỗi', colors.dangerInk, colors.dangerSoft),
};

/** Kết quả máy soi (cột verdict của bài/bình luận/media). */
export const VERDICT_STYLE: Record<string, PillStyle> = {
  PENDING: tone('Đang soi', colors.infoInk, colors.infoSoft),
  OK: tone('Sạch', colors.successInk, colors.successSoft),
  REVIEW: tone('Cần xem', colors.warningInk, colors.warningSoft),
  BLOCK: tone('Bị chặn', colors.dangerInk, colors.dangerSoft),
  SKIPPED: tone('Chưa soi', colors.textSecondary, colors.surfaceMuted),
  // Kết quả trong sổ / thử câu (chữ thường).
  ok: tone('Sạch', colors.successInk, colors.successSoft),
  review: tone('Cần xem', colors.warningInk, colors.warningSoft),
  block: tone('Chặn', colors.dangerInk, colors.dangerSoft),
  skipped: tone('Bỏ qua', colors.textSecondary, colors.surfaceMuted),
  error: tone('Lỗi', colors.dangerInk, colors.dangerSoft),
};

export const KEYWORD_REVIEW_TEXT = 'Có từ cần xem';

export const WORD_SEVERITY_STYLE: Record<WordSeverity, PillStyle> = {
  BLOCK: tone('Chặn hẳn', colors.dangerInk, colors.dangerSoft),
  REVIEW: tone('Cần xem', colors.warningInk, colors.warningSoft),
  ALLOW: tone('Ngoại lệ', colors.textSecondary, colors.surfaceMuted),
};

export const WORD_SEVERITY_HINT: Record<WordSeverity, string> = {
  BLOCK: 'Chặn hẳn — không cho đăng',
  REVIEW: 'Cần xem — vẫn đăng nhưng vào tab Cần xem',
  ALLOW: 'Ngoại lệ — không bao giờ bắt',
};

export const TIER_LABEL: Record<FeedTier, string> = {
  ADMIN: 'Admin',
  PAGE_EDITOR: 'Biên tập trang',
  SMOD: 'MOD',
};

export const TIER_RANK: Record<FeedTier, number> = { ADMIN: 3, PAGE_EDITOR: 2, SMOD: 1 };

export const MEMBER_STATUS_STYLE: Record<string, PillStyle> = {
  active: tone('Đã xác nhận', colors.successInk, colors.successSoft),
  candidate: tone('Ứng viên', colors.warningInk, colors.warningSoft),
  revoked: tone('Đã thu hồi', colors.textSecondary, colors.surfaceMuted),
};

export const REPORT_STATUS_STYLE: Record<string, PillStyle> = {
  OPEN: tone('Chờ xử lý', colors.dangerInk, colors.dangerSoft),
  RESOLVED: tone('Đã xử lý', colors.successInk, colors.successSoft),
  DISMISSED: tone('Đã bỏ qua', colors.textSecondary, colors.surfaceMuted),
};

export const RESOLUTION_LABEL: Record<string, string> = {
  DISMISS: 'Không vi phạm',
  HIDE: 'Đã ẩn',
  DELETE: 'Đã xoá',
  RESTRICT: 'Hạn chế người đăng',
};

/**
 * Văn mẫu phản hồi NGƯỜI BÁO CÁO (hộp "Xử lý báo cáo"). Câu đầu mỗi nhóm được điền sẵn và
 * trùng FEED_REPORT_REPLY_DEFAULTS của backend (src/feed/feed.constants.ts — câu dùng khi để
 * trống): sửa một bên thì sửa cả bên kia. Câu kết thúc bằng "lý do: " → Admin gõ tiếp.
 */
export type ReportReplyGroup = 'VIOLATION' | 'NO_VIOLATION';

export const REPORT_REPLY_TEMPLATES: Record<ReportReplyGroup, { label: string; text: string }[]> = {
  VIOLATION: [
    { label: 'Đã xử lý vi phạm', text: 'Cảm ơn bạn đã báo cáo chi tiết. Chúng tôi đã phát hiện vi phạm và đã xử lý.' },
    {
      label: 'Vi phạm Quy tắc cộng đồng',
      text: 'Cảm ơn bạn đã báo cáo. Nội dung vi phạm Quy tắc cộng đồng và đã được xử lý theo quy định.',
    },
    { label: 'Đã xử lý + ghi lý do', text: 'Cảm ơn bạn đã báo cáo. Chúng tôi đã xử lý vi phạm, lý do: ' },
  ],
  NO_VIOLATION: [
    {
      label: 'Chưa đủ vi phạm',
      text: 'Cảm ơn bạn đã báo cáo. Sau khi xem xét, nội dung này chưa đủ vi phạm Quy tắc cộng đồng.',
    },
    { label: 'Chưa đủ vi phạm + ghi lý do', text: 'Cảm ơn bạn đã báo cáo. Nội dung này chưa đủ vi phạm, lý do: ' },
    {
      label: 'Tiếp tục theo dõi',
      text: 'Cảm ơn bạn đã báo cáo. Nội dung chưa đủ vi phạm, chúng tôi sẽ tiếp tục theo dõi.',
    },
  ],
};

/** Không vi phạm (DISMISS) → nhóm "chưa đủ vi phạm"; ẩn / xoá / hạn chế → nhóm "vi phạm". */
export const replyGroupOf = (action: string): ReportReplyGroup => (action === 'DISMISS' ? 'NO_VIOLATION' : 'VIOLATION');

export const TARGET_TYPE_LABEL: Record<string, string> = {
  POST: 'Bài viết',
  COMMENT: 'Bình luận',
  USER: 'Tài khoản',
  MEDIA: 'Ảnh/video',
  REPORT: 'Báo cáo',
  SETTINGS: 'Cài đặt',
  WORDS: 'Từ cấm',
  TERMS: 'Quy tắc đăng bài',
};

export const TARGET_TYPE_STYLE: Record<string, PillStyle> = {
  POST: tone('Bài viết', colors.accentDeep, colors.accentSoft),
  COMMENT: tone('Bình luận', colors.infoInk, colors.infoSoft),
  USER: tone('Tài khoản', colors.purpleInk, colors.purpleSoft),
};

export const RESTRICT_SCOPE_LABEL: Record<RestrictionScope, string> = {
  POST: 'Không được đăng bài',
  COMMENT: 'Không được bình luận',
  ALL: 'Không đăng, bình luận, thả cảm xúc',
};

export const RESTRICT_SCOPE_OPTIONS: { value: RestrictionScope; label: string }[] = [
  { value: 'POST', label: 'đăng bài' },
  { value: 'COMMENT', label: 'bình luận' },
  { value: 'ALL', label: 'đăng, bình luận và thả cảm xúc' },
];

export const RESTRICT_DAY_OPTIONS: { value: string; label: string }[] = [
  { value: '1', label: '1 ngày' },
  { value: '3', label: '3 ngày' },
  { value: '7', label: '7 ngày' },
  { value: '30', label: '30 ngày' },
  { value: 'forever', label: 'Vô thời hạn' },
  { value: 'custom', label: 'Tuỳ chọn' },
];

// ------------------------------------------------------------------ lớp người dùng

export const CLASS_LABEL: Record<FeedClass, string> = {
  MANAGEMENT: 'Quản lý',
  AGENCY: 'Agency',
  CREATOR: 'Creator',
  STAFF: 'Nhân viên',
  PUBLIC: 'User',
};

/** Giải thích một dòng cho bảng quyền (Cài đặt → Quyền). */
export const CLASS_HINT: Record<FeedClass, string> = {
  MANAGEMENT: 'Admin và mọi MOD.',
  AGENCY: 'Tài khoản có vai trò Agency.',
  CREATOR: 'Creator và Studio Creator (vai trò do Admin đặt).',
  STAFF: 'Nhân viên, Creator NPC có vai trò User — chỉ người Admin đã xác nhận ở tab Thành viên.',
  PUBLIC: 'Fan, khách, người tự khai phòng ban mà chưa được xác nhận, tài khoản bị khoá.',
};

/** Token miễn giữ bài (01 §8.3). */
export const EXEMPT_OPTIONS: { value: string; label: string }[] = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'PAGE_EDITOR', label: 'Biên tập trang HP Media' },
  { value: 'SMOD', label: 'MOD khác' },
  { value: 'AGENCY', label: 'Agency' },
  { value: 'CREATOR', label: 'Creator' },
  { value: 'STAFF', label: 'Nhân viên' },
  { value: 'PAGE', label: 'Bài đăng tên HP Media' },
];

// ------------------------------------------------------------------ sổ kiểm duyệt

export const STAGE_LABEL: Record<string, string> = {
  KEYWORD: 'Từ cấm',
  AI_TEXT: 'AI soi chữ',
  AI_IMAGE: 'AI soi ảnh',
  AI_VIDEO: 'AI soi video',
  HUMAN: 'Quản lý',
  USER: 'Người dùng',
  SYSTEM: 'Hệ thống',
};

export const LOG_ACTION_LABEL: Record<string, string> = {
  CHECK: 'Máy soi',
  SUBMIT_BLOCKED: 'Chặn khi đăng (từ cấm)',
  CREATE: 'Đăng bài',
  EDIT: 'Sửa',
  DELETE: 'Tác giả xoá',
  REPORT: 'Báo cáo',
  AUTO_HIDE_REPORTS: 'Tự ẩn vì nhiều báo cáo',
  AI_HIDE: 'Máy ẩn',
  AI_PENDING: 'Máy giữ chờ duyệt',
  AI_FLAG: 'Máy gắn cờ cần xem',
  MEDIA_FAILED: 'Video lỗi',
  BLOCK: 'Người dùng chặn',
  UNBLOCK: 'Bỏ chặn',
  MEMBER_GRANT: 'Xác nhận thành viên',
  MEMBER_REVOKE: 'Thu hồi thành viên',
  PAGE_EDITOR_ADD: 'Thêm Biên tập trang',
  PAGE_EDITOR_REMOVE: 'Bỏ Biên tập trang',
  TERMS_VERSION: 'Quy tắc phiên bản mới',
  HIDE: 'Quản lý ẩn',
  UNHIDE: 'Hiện lại',
  APPROVE: 'Duyệt',
  REJECT: 'Từ chối',
  MOD_DELETE: 'Quản lý xoá',
  PIN: 'Ghim',
  UNPIN: 'Bỏ ghim',
  IMPORTANT_ON: 'Đánh dấu Quan trọng',
  IMPORTANT_OFF: 'Bỏ Quan trọng',
  REPORT_RESOLVE: 'Xử lý báo cáo',
  RESTRICT: 'Hạn chế',
  UNRESTRICT: 'Gỡ hạn chế',
  SETTINGS_UPDATE: 'Đổi cài đặt',
  WORDS_UPDATE: 'Đổi từ cấm',
  ACCOUNT_DELETED: 'Tài khoản đã xoá',
};

/** Nhãn nhỏ đặc biệt trong `labels` của sổ. */
export const LOG_LABEL_TEXT: Record<string, string> = {
  no_consent: 'Chưa đồng ý Quy tắc có câu AI — không gửi đi soi',
  ai_error: 'Máy soi lỗi',
};

export const LOG_VERDICT_OPTIONS = ['ok', 'review', 'block', 'skipped', 'error'];

export const DELETE_REASON_LABEL: Record<string, string> = {
  AUTHOR: 'tác giả tự xoá',
  MODERATOR: 'quản lý xoá',
  ACCOUNT_DELETED: 'tài khoản đã bị xoá',
};

export const REACTION_LABEL: Record<string, string> = {
  LIKE: 'Thích',
  LOVE: 'Yêu thích',
  HAHA: 'Haha',
  WOW: 'Wow',
  SAD: 'Buồn',
};

/** Dự phòng khi D1/A1 chưa trả danh sách lý do (01 §5.3 FEED_REPORT_REASONS). */
export const REPORT_REASON_FALLBACK: { value: string; label: string }[] = [
  { value: 'SPAM', label: 'Spam, quảng cáo' },
  { value: 'HARASSMENT', label: 'Quấy rối, bắt nạt' },
  { value: 'HATE', label: 'Thù ghét, phân biệt đối xử' },
  { value: 'SEXUAL', label: 'Khoả thân, tình dục' },
  { value: 'VIOLENCE', label: 'Bạo lực, ghê rợn' },
  { value: 'SCAM', label: 'Lừa đảo' },
  { value: 'FALSE_INFO', label: 'Thông tin sai sự thật' },
  { value: 'PRIVACY', label: 'Lộ thông tin cá nhân' },
  { value: 'CONFIDENTIAL', label: 'Lộ thông tin nội bộ công ty' },
  { value: 'SELF_HARM', label: 'Tự gây hại' },
  { value: 'OTHER', label: 'Lý do khác' },
];

/** Gợi ý ô "Lý do" khi ẩn/từ chối/xoá (03 §7.3). */
export const MOD_REASON_SUGGESTIONS = [
  'Nội dung phản cảm',
  'Spam, quảng cáo',
  'Xúc phạm người khác',
  'Lộ thông tin nội bộ công ty',
  'Lộ thông tin cá nhân',
  'Thông tin sai sự thật',
];

// ------------------------------------------------------------------ AI (GĐ1b)

/** Mục của OpenAI omni-moderation (03 §14.7). */
export const OPENAI_CATEGORIES: { key: string; label: string }[] = [
  { key: 'sexual', label: 'Tình dục' },
  { key: 'sexual/minors', label: 'Tình dục liên quan trẻ vị thành niên' },
  { key: 'harassment', label: 'Quấy rối' },
  { key: 'harassment/threatening', label: 'Quấy rối kèm đe doạ' },
  { key: 'hate', label: 'Thù ghét' },
  { key: 'hate/threatening', label: 'Thù ghét kèm đe doạ' },
  { key: 'illicit', label: 'Hành vi phạm pháp' },
  { key: 'illicit/violent', label: 'Phạm pháp có bạo lực' },
  { key: 'self-harm', label: 'Tự gây hại' },
  { key: 'self-harm/intent', label: 'Ý định tự gây hại' },
  { key: 'self-harm/instructions', label: 'Hướng dẫn tự gây hại' },
  { key: 'violence', label: 'Bạo lực' },
  { key: 'violence/graphic', label: 'Bạo lực ghê rợn' },
];

/** Ngưỡng mặc định của 04 §C2 — chỉ để hiện khi máy chủ chưa trả ngưỡng đang dùng. */
export const DEFAULT_AI_THRESHOLDS = {
  openai: {
    block: { 'sexual/minors': 0.3, sexual: 0.9, 'violence/graphic': 0.9 } as Record<string, number>,
    review: 0.5,
  },
  // Khớp backend src/feed/moderation/moderation.types.ts (DEFAULT_AI_THRESHOLDS).
  claude: { blockMinScore: 0 },
};

export const AI_PROVIDER_LABEL: Record<string, string> = {
  openai: 'OpenAI (ảnh + chữ, miễn phí)',
  claude: 'Claude Haiku (chữ tiếng Việt)',
  mock: 'Mock (chỉ máy dev)',
  keyword: 'Từ cấm',
  none: 'Không có',
};

/** Câu về AI phải có trong Quy tắc đăng bài trước khi bật AI (04 §C7). */
export const AI_TERMS_CLAUSE =
  'Chữ, ảnh, video bạn đăng có thể được gửi tới dịch vụ AI của OpenAI/Anthropic (Mỹ) để tự động kiểm tra vi phạm. Họ không dùng dữ liệu này để huấn luyện AI và xoá trong vòng 30 ngày; nội dung bị gắn cờ vi phạm có thể được lưu lâu hơn.';

// ------------------------------------------------------------------ bảo trì (D30)

export const MAINTENANCE_TASKS: { task: string; label: string; hint: string }[] = [
  {
    task: 'watchdog',
    label: 'Kiểm tra video/ảnh bị treo',
    hint: 'Đẩy lại video kẹt quá 30 phút (quá 3 lần thì đánh dấu lỗi); đẩy lại ảnh/chữ chưa soi xong.',
  },
  { task: 'orphans', label: 'Dọn ảnh/video tải lên nhưng không đăng', hint: 'Quá 24 giờ, xoá hẳn file.' },
  { task: 'temp', label: 'Dọn file tạm trên máy chủ', hint: 'Thư mục FEED_TMP_DIR.' },
  {
    task: 'purge',
    label: 'Xoá file hết hạn giữ',
    hint: 'File bài tác giả tự xoá (không bị báo cáo) sau 30 ngày; file nội dung bị gỡ sau hạn bằng chứng (365 ngày, chờ luật sư). Dòng dữ liệu vẫn giữ.',
  },
  { task: 'recount', label: 'Đếm lại cảm xúc, bình luận', hint: 'Mặc định chỉ 30 ngày gần nhất.' },
  { task: 'deleted-accounts', label: 'Ẩn bài của tài khoản đã xoá', hint: '' },
  { task: 'report-sla', label: 'Gửi nhắc báo cáo quá hạn', hint: 'Cho Admin và Biên tập trang.' },
];

export const NO_PAGE_TOOLTIP = 'Bài tên HP Media chỉ Admin hoặc Biên tập trang xử lý được.';
